import React, { useState, useEffect } from 'react';
import { format, parseISO } from 'date-fns';
import { useAuth } from '../../../contexts/AuthContext';
import { collection, query, where, addDoc, updateDoc, doc, deleteDoc, onSnapshot } from 'firebase/firestore';
import { db } from '../../../utils/firebase';
import { Event, EventFormData } from '../../../types/event';
import { isTeacher } from '../../../utils/roleChecks';
import Button from '../../../components/ui/Button';
import InputField from '../../../components/ui/InputField';

const EventManagement: React.FC = () => {
  const { currentUser } = useAuth();
  const teacher = currentUser;
  const [events, setEvents] = useState<Event[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState<Event | null>(null);
  const [formData, setFormData] = useState<EventFormData & { currentAmount?: number }>({
    title: '',
    description: '',
    startDate: new Date(),
    endDate: new Date(),
    eventType: 'class',
    eventSubType: 'notice',
    targetAmount: 0,
    currentAmount: 0,
    isActive: true
  });

  useEffect(() => {
    if (!teacher?.uid || !isTeacher(teacher)) return;

    try {
      setLoading(true);
      const eventsRef = collection(db, `schools/${teacher.schoolId}/events`);
      const q = query(
        eventsRef,
        where('teacherId', '==', teacher.uid)
      );

      const unsubscribe = onSnapshot(q, (snapshot) => {
        const eventsData = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
          startDate: doc.data().startDate.toDate(),
          endDate: doc.data().endDate.toDate(),
          createdAt: doc.data().createdAt.toDate(),
          updatedAt: doc.data().updatedAt.toDate()
        })) as Event[];

        setEvents(eventsData);
        setLoading(false);
      }, (error) => {
        console.error('이벤트 목록 로딩 중 오류:', error);
        setError('이벤트 목록을 불러오는 중 오류가 발생했습니다.');
        setLoading(false);
      });

      return () => unsubscribe();
    } catch (error) {
      console.error('데이터 로딩 중 오류:', error);
      setError('데이터를 불러오는 중 오류가 발생했습니다.');
      setLoading(false);
    }
  }, [teacher?.uid]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!teacher?.uid || !isTeacher(teacher)) return;

    try {
      const { uid, name, schoolId } = teacher;
      if (!uid || !name || !schoolId) {
        setError('필수 정보가 누락되었습니다. 다시 로그인해주세요.');
        return;
      }

      if (editMode && selectedEvent) {
        const eventRef = doc(db, `schools/${schoolId}/events/${selectedEvent.id}`);
        await updateDoc(eventRef, {
          ...formData,
          updatedAt: new Date()
        });
        setSuccess('이벤트가 수정되었습니다.');
      } else {
        const newEvent = {
          ...formData,
          teacherId: uid,
          teacherName: name,
          schoolId,
          classId: teacher.classId || '',
          isActive: true,
          currentAmount: formData.eventSubType === 'donation' ? 0 : null,
          createdAt: new Date(),
          updatedAt: new Date()
        };

        console.log('Creating new event:', newEvent);

        const eventsRef = collection(db, `schools/${schoolId}/events`);
        await addDoc(eventsRef, newEvent);
        setSuccess('새 이벤트가 생성되었습니다.');
      }

      setOpenDialog(false);
      setEditMode(false);
      setSelectedEvent(null);
      setFormData({
        title: '',
        description: '',
        startDate: new Date(),
        endDate: new Date(),
        eventType: 'class',
        eventSubType: 'notice',
        targetAmount: 0,
        currentAmount: 0,
        isActive: true
      });
    } catch (err) {
      console.error('이벤트 처리 중 오류:', err);
      setError(editMode ? '이벤트 수정에 실패했습니다.' : '이벤트 생성에 실패했습니다.');
    }
  };

  const handleEdit = (event: Event) => {
    setSelectedEvent(event);
    setFormData({
      title: event.title,
      description: event.description,
      startDate: event.startDate,
      endDate: event.endDate,
      eventType: event.eventType,
      eventSubType: event.eventSubType,
      targetAmount: event.targetAmount,
      currentAmount: event.currentAmount,
      isActive: event.isActive
    });
    setEditMode(true);
    setOpenDialog(true);
  };

  const handleDelete = async (eventId: string) => {
    if (!teacher?.uid || !isTeacher(teacher)) return;

    try {
      const eventRef = doc(db, `schools/${teacher.schoolId}/events/${eventId}`);
      await deleteDoc(eventRef);
      setSuccess('이벤트가 삭제되었습니다.');
    } catch (err) {
      console.error('이벤트 삭제 중 오류:', err);
      setError('이벤트 삭제에 실패했습니다.');
    }
  };

  const handleToggleActive = async (eventId: string, isActive: boolean) => {
    if (!teacher?.uid || !isTeacher(teacher)) return;

    try {
      const eventRef = doc(db, `schools/${teacher.schoolId}/events/${eventId}`);
      await updateDoc(eventRef, {
        isActive: !isActive,
        updatedAt: new Date()
      });
      setSuccess(`이벤트가 ${!isActive ? '활성화' : '비활성화'}되었습니다.`);
    } catch (err) {
      console.error('이벤트 상태 변경 중 오류:', err);
      setError('이벤트 상태 변경에 실패했습니다.');
    }
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditMode(false);
    setSelectedEvent(null);
    setFormData({
      title: '',
      description: '',
      startDate: new Date(),
      endDate: new Date(),
      eventType: 'class',
      eventSubType: 'notice',
      targetAmount: 0,
      currentAmount: 0,
      isActive: true
    });
  };

  if (!teacher || !isTeacher(teacher)) {
    return (
      <div style={{ padding: '24px' }}>
        <div
          style={{
            padding: '16px',
            backgroundColor: '#FFEBEE',
            color: '#C62828',
            borderRadius: '8px',
            border: '1px solid #FFCDD2',
          }}
        >
          교사 권한이 필요합니다.
        </div>
      </div>
    );
  }

  const toDateStr = (d: Date) => format(d, 'yyyy-MM-dd');
  const fromDateStr = (s: string) => (s ? parseISO(s) : new Date());

  return (
    <div style={{ padding: '24px' }}>
      <div
        style={{
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '24px',
        }}
      >
        <div
          style={{
            fontSize: '24px',
            fontWeight: 500,
            lineHeight: '36px',
            color: 'var(--Color-text-strong)',
          }}
        >
          이벤트 관리
        </div>
        <Button
          variant="primary"
          onClick={() => {
            setEditMode(false);
            setOpenDialog(true);
          }}
        >
          새 이벤트 생성
        </Button>
      </div>

      {error && (
        <div
          style={{
            padding: '16px',
            marginBottom: '16px',
            backgroundColor: '#FFEBEE',
            color: '#C62828',
            borderRadius: '8px',
            border: '1px solid #FFCDD2',
          }}
        >
          {error}
        </div>
      )}

      {success && (
        <div
          style={{
            padding: '16px',
            marginBottom: '16px',
            backgroundColor: '#E8F5E9',
            color: '#2E7D32',
            borderRadius: '8px',
            border: '1px solid #C8E6C9',
          }}
        >
          {success}
        </div>
      )}

      {loading ? (
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            padding: '24px',
            color: 'var(--Color-text-normal)',
          }}
        >
          로딩 중...
        </div>
      ) : (
        <div
          style={{
            overflow: 'auto',
            border: '1px solid var(--Color-line-normal)',
            borderRadius: '8px',
            backgroundColor: 'var(--Color-background-normal)',
          }}
        >
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ backgroundColor: 'var(--Color-fill-hover)' }}>
                <th style={tableHeaderStyle}>제목</th>
                <th style={tableHeaderStyle}>설명</th>
                <th style={tableHeaderStyle}>시작일</th>
                <th style={tableHeaderStyle}>종료일</th>
                <th style={tableHeaderStyle}>유형</th>
                <th style={tableHeaderStyle}>상태</th>
                <th style={tableHeaderStyle}>관리</th>
              </tr>
            </thead>
            <tbody>
              {events.map((event) => (
                <tr key={event.id} style={{ borderTop: '1px solid var(--Color-line-normal)' }}>
                  <td style={tableCellStyle}>{event.title}</td>
                  <td style={tableCellStyle}>{event.description}</td>
                  <td style={tableCellStyle}>{event.startDate.toLocaleDateString()}</td>
                  <td style={tableCellStyle}>{event.endDate.toLocaleDateString()}</td>
                  <td style={tableCellStyle}>
                    {event.eventType === 'class' ? '학급' : '교과'}
                  </td>
                  <td style={tableCellStyle}>
                    {event.isActive ? '활성' : '비활성'}
                  </td>
                  <td style={tableCellStyle}>
                    <div style={{ display: 'flex', flexDirection: 'row', gap: '8px', flexWrap: 'wrap' }}>
                      <Button variant="outline" size="small" onClick={() => handleEdit(event)}>
                        수정
                      </Button>
                      <Button
                        variant="outline"
                        size="small"
                        onClick={() => handleToggleActive(event.id, event.isActive)}
                      >
                        {event.isActive ? '비활성화' : '활성화'}
                      </Button>
                      <Button variant="cancel" size="small" onClick={() => handleDelete(event.id)}>
                        삭제
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {openDialog && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
          }}
          onClick={handleCloseDialog}
        >
          <div
            style={{
              backgroundColor: 'var(--Color-background-normal)',
              borderRadius: '12px',
              padding: '24px',
              maxWidth: '480px',
              width: '90%',
              maxHeight: '90vh',
              overflow: 'auto',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              style={{
                fontSize: '20px',
                fontWeight: 600,
                marginBottom: '20px',
                color: 'var(--Color-text-strong)',
              }}
            >
              {editMode ? '이벤트 수정' : '새 이벤트 생성'}
            </div>

            <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <InputField
                label="제목"
                value={formData.title}
                onChange={(v) => setFormData(prev => ({ ...prev, title: v }))}
                required
                width="100%"
              />
              <div>
                <label
                  style={{
                    display: 'block',
                    fontSize: '14px',
                    fontWeight: 500,
                    marginBottom: '8px',
                    color: 'var(--Color-text-normal)',
                  }}
                >
                  설명 *
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  required
                  rows={4}
                  style={{
                    width: '100%',
                    padding: '12px',
                    fontSize: '14px',
                    border: '1px solid var(--Color-line-normal)',
                    borderRadius: '8px',
                    boxSizing: 'border-box',
                  }}
                />
              </div>
              <div>
                <label
                  style={{
                    display: 'block',
                    fontSize: '14px',
                    fontWeight: 500,
                    marginBottom: '8px',
                    color: 'var(--Color-text-normal)',
                  }}
                >
                  시작일
                </label>
                <input
                  type="date"
                  value={toDateStr(formData.startDate)}
                  onChange={(e) => setFormData(prev => ({ ...prev, startDate: fromDateStr(e.target.value) }))}
                  style={{
                    width: '100%',
                    padding: '12px',
                    fontSize: '14px',
                    border: '1px solid var(--Color-line-normal)',
                    borderRadius: '8px',
                    boxSizing: 'border-box',
                  }}
                />
              </div>
              <div>
                <label
                  style={{
                    display: 'block',
                    fontSize: '14px',
                    fontWeight: 500,
                    marginBottom: '8px',
                    color: 'var(--Color-text-normal)',
                  }}
                >
                  종료일
                </label>
                <input
                  type="date"
                  value={toDateStr(formData.endDate)}
                  onChange={(e) => setFormData(prev => ({ ...prev, endDate: fromDateStr(e.target.value) }))}
                  style={{
                    width: '100%',
                    padding: '12px',
                    fontSize: '14px',
                    border: '1px solid var(--Color-line-normal)',
                    borderRadius: '8px',
                    boxSizing: 'border-box',
                  }}
                />
              </div>
              <div>
                <label
                  style={{
                    display: 'block',
                    fontSize: '14px',
                    fontWeight: 500,
                    marginBottom: '8px',
                    color: 'var(--Color-text-normal)',
                  }}
                >
                  이벤트 유형
                </label>
                <select
                  value={formData.eventType}
                  onChange={(e) => setFormData(prev => ({ ...prev, eventType: e.target.value as 'class' | 'subject' }))}
                  style={{
                    width: '100%',
                    padding: '12px',
                    fontSize: '14px',
                    border: '1px solid var(--Color-line-normal)',
                    borderRadius: '8px',
                    boxSizing: 'border-box',
                  }}
                >
                  <option value="class">학급</option>
                  <option value="subject">교과</option>
                </select>
              </div>
              <div>
                <label
                  style={{
                    display: 'block',
                    fontSize: '14px',
                    fontWeight: 500,
                    marginBottom: '8px',
                    color: 'var(--Color-text-normal)',
                  }}
                >
                  이벤트 하위 유형
                </label>
                <select
                  value={formData.eventSubType}
                  onChange={(e) => setFormData(prev => ({ ...prev, eventSubType: e.target.value as 'notice' | 'donation' }))}
                  style={{
                    width: '100%',
                    padding: '12px',
                    fontSize: '14px',
                    border: '1px solid var(--Color-line-normal)',
                    borderRadius: '8px',
                    boxSizing: 'border-box',
                  }}
                >
                  <option value="notice">공지형</option>
                  <option value="donation">기부형</option>
                </select>
              </div>
              {formData.eventSubType === 'donation' && (
                <InputField
                  type="number"
                  label="목표 포인트"
                  value={String(formData.targetAmount ?? 0)}
                  onChange={(v) => setFormData(prev => ({ ...prev, targetAmount: Number(v) }))}
                  required
                  width="100%"
                />
              )}

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '8px' }}>
                <Button type="button" variant="cancel" onClick={handleCloseDialog}>
                  취소
                </Button>
                <Button type="submit" variant="primary">
                  {editMode ? '수정' : '생성'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

const tableHeaderStyle: React.CSSProperties = {
  padding: '12px 16px',
  textAlign: 'left',
  fontSize: '14px',
  fontWeight: 600,
  color: 'var(--Color-text-strong)',
};

const tableCellStyle: React.CSSProperties = {
  padding: '12px 16px',
  fontSize: '14px',
  color: 'var(--Color-text-normal)',
};

export default EventManagement;
